import type { BBox, LatLon } from './types.js';

/** WGS84 mean radius, metres. */
export const EARTH_RADIUS = 6_371_008.8;
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

/**
 * A local tangent-plane ("equirectangular") projection anchored at the region centre.
 *
 * Over a few square kilometres the error against a proper geodesic is a few centimetres,
 * which is far below the resolution anyone can perceive while sliding a car through a
 * junction — and unlike a global Web Mercator projection it keeps every coordinate
 * within ±1500 or so, which is exactly where float32 vertex buffers are happiest.
 *
 * Axis convention matches Three.js: +X is east, +Y is up, +Z is *south*, so that a car
 * with heading 0 pointing down -Z is driving north.
 */
export class Projection {
  readonly origin: LatLon;
  /** metres per degree of longitude at the origin latitude */
  readonly mPerLon: number;
  /** metres per degree of latitude */
  readonly mPerLat: number;

  constructor(origin: LatLon) {
    // Round the origin so that the same bbox always yields bit-identical projections
    // regardless of how the centre was computed upstream.
    this.origin = { lat: round7(origin.lat), lon: round7(origin.lon) };
    this.mPerLat = EARTH_RADIUS * DEG2RAD;
    this.mPerLon = EARTH_RADIUS * DEG2RAD * Math.cos(this.origin.lat * DEG2RAD);
  }

  static forBBox(bbox: BBox): Projection {
    return new Projection(bboxCentre(bbox));
  }

  /** lat/lon -> local metres. */
  project(p: LatLon): { x: number; z: number } {
    return {
      x: (p.lon - this.origin.lon) * this.mPerLon,
      z: -(p.lat - this.origin.lat) * this.mPerLat,
    };
  }

  /** local metres -> lat/lon. */
  unproject(x: number, z: number): LatLon {
    return {
      lat: this.origin.lat - z / this.mPerLat,
      lon: this.origin.lon + x / this.mPerLon,
    };
  }

  /** The projected half-extent of a bbox, in metres. */
  extentOf(bbox: BBox): { x: number; z: number } {
    const a = this.project({ lat: bbox.north, lon: bbox.west });
    const b = this.project({ lat: bbox.south, lon: bbox.east });
    return { x: Math.abs(b.x - a.x) / 2, z: Math.abs(b.z - a.z) / 2 };
  }
}

function round7(n: number): number {
  return Math.round(n * 1e7) / 1e7;
}

export function bboxCentre(bbox: BBox): LatLon {
  return { lat: (bbox.south + bbox.north) / 2, lon: (bbox.west + bbox.east) / 2 };
}

/** Ground area of a bbox in square kilometres. */
export function bboxAreaKm2(bbox: BBox): number {
  const midLat = ((bbox.south + bbox.north) / 2) * DEG2RAD;
  const h = (bbox.north - bbox.south) * DEG2RAD * EARTH_RADIUS;
  const w = (bbox.east - bbox.west) * DEG2RAD * EARTH_RADIUS * Math.cos(midLat);
  return Math.abs(h * w) / 1e6;
}

export function bboxSpanMetres(bbox: BBox): { width: number; height: number } {
  const midLat = ((bbox.south + bbox.north) / 2) * DEG2RAD;
  return {
    width: Math.abs((bbox.east - bbox.west) * DEG2RAD * EARTH_RADIUS * Math.cos(midLat)),
    height: Math.abs((bbox.north - bbox.south) * DEG2RAD * EARTH_RADIUS),
  };
}

export function bboxContains(bbox: BBox, p: LatLon): boolean {
  return (
    p.lat >= bbox.south && p.lat <= bbox.north && p.lon >= bbox.west && p.lon <= bbox.east
  );
}

/** Grows a bbox by a margin in metres, clamped to legal lat/lon. */
export function expandBBox(bbox: BBox, metres: number): BBox {
  const midLat = ((bbox.south + bbox.north) / 2) * DEG2RAD;
  const dLat = (metres / EARTH_RADIUS) * RAD2DEG;
  const dLon = (metres / (EARTH_RADIUS * Math.cos(midLat))) * RAD2DEG;
  return {
    south: Math.max(-85, bbox.south - dLat),
    north: Math.min(85, bbox.north + dLat),
    west: Math.max(-180, bbox.west - dLon),
    east: Math.min(180, bbox.east + dLon),
  };
}
